import { type CatalogPlan, type PlanPrice } from '@/api/schemas/catalog'

/**
 * Display-side pricing derivations for the public site. All values stay in
 * integer cents; this is rendering math, not billing math — the backend owns
 * every charged amount.
 */

/** Annual billing discount shown across the marketing site ("−20%"). */
export const ANNUAL_DISCOUNT = 0.2

export function annualMonthlyCents(monthlyCents: number): number {
  return Math.round(monthlyCents * (1 - ANNUAL_DISCOUNT))
}

export function annualTotalCents(monthlyCents: number): number {
  return annualMonthlyCents(monthlyCents) * 12
}

export interface ResolvedPrice {
  cents: number
  currency: string
  /** True when the selected currency had no localized price for this plan. */
  isBaseFallback: boolean
}

/**
 * Price for a plan in the selected currency, falling back to the plan's base
 * price when no localized price exists (UI_PLAN Screen 2).
 */
export function resolvePlanPrice(
  plan: CatalogPlan,
  currencyCode: string,
  prices: PlanPrice[] | undefined,
): ResolvedPrice {
  const localized = prices?.find(
    (price) => price.planSlug === plan.slug && price.currencyCode === currencyCode,
  )
  if (localized) {
    return { cents: localized.priceCents, currency: currencyCode, isBaseFallback: false }
  }
  return { cents: plan.priceCents, currency: plan.currency, isBaseFallback: true }
}

export function planMauCap(plan: CatalogPlan): number | null {
  const raw = plan.features['max_users']
  if (!raw) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Smallest plan whose MAU cap covers the given usage; null means the usage
 * exceeds every listed plan (→ Enterprise).
 */
export function recommendPlan(mau: number, plans: CatalogPlan[]): CatalogPlan | null {
  const candidates = plans
    .map((plan) => ({ plan, cap: planMauCap(plan) }))
    .filter((entry): entry is { plan: CatalogPlan; cap: number } => entry.cap !== null)
    .sort((a, b) => a.cap - b.cap)
  return candidates.find((entry) => entry.cap >= mau)?.plan ?? null
}

/** Usage of the plan's MAU cap as a 0–100 percentage (null when uncapped). */
export function capUsagePercent(mau: number, plan: CatalogPlan): number | null {
  const cap = planMauCap(plan)
  if (!cap) return null
  return Math.min(100, Math.round((mau / cap) * 100))
}
